import { useEffect, useMemo, useRef, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { IoAdd, IoClose } from "react-icons/io5";
import { Oval } from "react-loader-spinner";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";

import WOFormService from "src/Services/API/woform.service";
import FormTemplateService from "src/Services/API/formtemplate.service";
import WOService from "src/Services/API/wo.service";
import UserService from "src/Services/API/user.service";
import { handleError } from "src/Common/function";
import { formatDate } from "src/Common/common";
import { getFormTypeLabel } from "src/Pages/forms/formEnums";
import TagInput from "./components/TagInput";

// Jobsite Forms are WoAttachment rows with type === 2 (single-table inheritance
// shares the table with technical documents/jobsite photos). The /attach upload
// flow uses type 1 ("attachments to the work order"); the per-file
// metadata.attachmentType then narrows it to a jobsite form.
const JOBSITE_FORM_TYPE = 2;
const ATTACHMENT_UPLOAD_FLOW = 1;

// The wizard steps. "menu" is the three-card chooser; the rest are the chosen
// sub-flows.
const STEP = {
  MENU: "menu",
  ASSIGN: "assign",
  PDF_PICK: "pdf-pick",
  PDF_META: "pdf-meta",
  PHOTO_PICK: "photo-pick",
  PHOTO_META: "photo-meta",
};

const PDF_ACCEPT = "application/pdf";
const PHOTO_ACCEPT = "image/jpeg,image/png,image/heic,image/heif";

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getCategoryLabel = (row) => getFormTypeLabel(row);

// Compress oversized images before upload, matching the WoDetail attachment
// upload flow. PDFs and small images pass through untouched.
const compressIfNeeded = async (file) => {
  const isImage = file.type?.startsWith("image/");
  if (!isImage || file.size <= 5242880) return file;
  const blob = await imageCompression(file, {
    maxSizeMB: 5,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  });
  return new File([blob], file.name, { type: blob.type, lastModified: Date.now() });
};

// One photo's editable metadata, seeded from the file name.
const makePhotoEntry = (file) => ({
  file,
  previewUrl: URL.createObjectURL(file),
  formName: file.name,
  tags: [],
  description: "",
  includeInServiceTicket: false,
});

const ServiceTicketToggle = ({ checked, onChange, label, helper }) => (
  <div className="flex items-start gap-3 pt-2">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-[24px] w-[44px] shrink-0 items-center rounded-[12px] p-[2px] transition-colors ${
        checked ? "bg-[#0065FD]" : "bg-[#EEF0F9]"
      } cursor-pointer`}
    >
      <span
        className={`inline-block h-[20px] w-[20px] transform rounded-full bg-white shadow-[0px_1px_2px_rgba(10,12.67,18,0.10)] transition-transform ${
          checked ? "translate-x-[20px]" : "translate-x-0"
        }`}
      />
    </button>
    <div className="flex flex-col gap-[2px]">
      <span className="font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332]">{label}</span>
      <span className="font-OpenSansRegular text-[16px] leading-[24px] text-[#7C829A]">{helper}</span>
    </div>
  </div>
);

const FieldLabel = ({ children }) => (
  <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
    {children}
  </span>
);

// `accept` only filters the file picker dialog, not drag-and-drop. This checks
// a file against the comma-separated accept list (MIME types) so dropped files
// are validated too. Falls back to the file extension when the browser reports
// no MIME type (e.g. HEIC on some browsers).
const isAcceptedFile = (file, accept) => {
  const allowed = accept.split(",").map((s) => s.trim().toLowerCase());
  const mime = (file.type || "").toLowerCase();
  if (mime && allowed.includes(mime)) return true;
  const ext = file.name?.split(".").pop()?.toLowerCase();
  const extByMime = {
    "application/pdf": ["pdf"],
    "image/jpeg": ["jpg", "jpeg"],
    "image/png": ["png"],
    "image/heic": ["heic"],
    "image/heif": ["heif"],
  };
  return allowed.some((type) => extByMime[type]?.includes(ext));
};

const Dropzone = ({ icon, title, hint, footer, accept, multiple, onFiles }) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const accepted = files.filter((file) => isAcceptedFile(file, accept));
    if (accepted.length < files.length) {
      toast(`Unsupported file type. ${footer}`);
    }
    if (accepted.length) onFiles(multiple ? accepted : [accepted[0]]);
  };

  return (
    <div className="p-6 flex flex-col gap-6">
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`w-full h-[280px] p-8 flex flex-col items-center justify-center rounded-[12px] outline outline-2 -outline-offset-2 cursor-pointer transition-colors ${
          dragActive ? "outline-[#0065FD] bg-[#F2F7FF]" : "outline-[#C9CEE2] bg-[#F8F9FE]"
        }`}
      >
        <div className="pb-4">
          <div className="w-[56px] h-[56px] rounded-full bg-white flex items-center justify-center shadow-[0px_1px_2px_rgba(0,0,0,0.05)]">
            {icon}
          </div>
        </div>
        <span className="pb-1 text-center font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332]">
          {title}
        </span>
        <span className="pb-4 text-center font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A]">
          {hint}
        </span>
        <div className="pb-4 w-[200px] max-w-[200px] flex items-center">
          <span className="flex-1 h-px bg-[#E5E8F3]" />
          <span className="px-3 text-center font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#868DA6]">
            OR
          </span>
          <span className="flex-1 h-px bg-[#E5E8F3]" />
        </div>
        <span className="h-[40px] px-4 flex items-center justify-center rounded-[8px] bg-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline outline-1 -outline-offset-1 outline-[#C9CEE2] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332]">
          Choose File
        </span>
      </div>
      <p className="text-center font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]">
        {footer}
      </p>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          if (e.target) e.target.value = null;
        }}
      />
    </div>
  );
};

const PdfFileIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 3H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7l-4-4Z" fill="#0065FD" />
    <path d="M14 3v4h4" fill="#9CC0FF" />
  </svg>
);

const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 3.5a.5.5 0 0 1 0 1H2a.5.5 0 0 1 0-1h12ZM2.83 13.33V4a.5.5 0 0 1 1 0v9.33c0 .17.09.38.27.56.18.18.39.27.56.27h6.67c.17 0 .38-.09.56-.27.18-.18.27-.39.27-.56V4a.5.5 0 0 1 1 0v9.33c0 .5-.24.95-.56 1.27-.32.32-.77.57-1.27.57H4.67c-.5 0-.95-.25-1.27-.57-.32-.32-.57-.77-.57-1.27Z"
      fill="#7C829A"
    />
    <path
      d="M10.17 4V2.67c0-.17-.09-.39-.27-.57a.86.86 0 0 0-.57-.27H6.67c-.17 0-.39.09-.57.27a.86.86 0 0 0-.27.57V4a.5.5 0 0 1-1 0V2.67c0-.5.24-.96.56-1.28.32-.32.77-.56 1.27-.56h2.67c.5 0 .95.24 1.27.56.32.32.56.78.56 1.28V4a.5.5 0 0 1-1 0Z"
      fill="#7C829A"
    />
  </svg>
);

const ModalShell = ({ title, subtitle, onClose, children, footer, width = "w-[640px]" }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
    <OutsideClickHandler onOutsideClick={onClose}>
      <div className={`${width} max-h-[90vh] flex flex-col bg-white rounded-[12px] overflow-hidden`}>
        <div className="px-6 py-5 border-b border-[#E5E8F3] flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-OpenSansSemiBold text-[20px] leading-[28px] capitalize text-[#1A2332]">{title}</h2>
            {subtitle && (
              <p className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A]">
                {subtitle}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-[#868DA6] cursor-pointer">
            <IoClose size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-[#E5E8F3] flex items-center justify-end gap-3">{footer}</div>
        )}
      </div>
    </OutsideClickHandler>
  </div>
);

const PrimaryButton = ({ children, onClick, disabled, loading }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className={`h-[40px] px-5 rounded-[8px] bg-[#0065FD] flex items-center justify-center gap-2 font-OpenSansSemiBold text-[14px] text-white ${
      disabled || loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
    }`}
  >
    {loading ? (
      <Oval height="16" width="16" color="#FFFFFF" strokeWidth={8} secondaryColor="#FFEEFF" visible />
    ) : (
      children
    )}
  </button>
);

const SecondaryButton = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-[40px] px-4 rounded-[8px] bg-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline outline-1 -outline-offset-1 outline-[#C9CEE2] flex items-center justify-center font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332] cursor-pointer"
  >
    {children}
  </button>
);

const GhostButton = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-[40px] px-1 rounded-[6px] flex items-center justify-center font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#7C829A] cursor-pointer"
  >
    {children}
  </button>
);

/**
 * "Add Form" flow for a Work Order. Three entry points:
 *  - Assign Digital Form: assign an existing unassigned wo_form to this WO.
 *  - Upload PDF Form / Upload Photo Forms: upload files as WoAttachment jobsite
 *    forms (type 2) with per-file metadata.
 *
 * `onAssigned` / `onUploaded` let the parent refresh the WO; the modal closes
 * itself after a successful action.
 */
const AddFormModal = ({ shown, order, onClose, onAssigned, onUploaded }) => {
  const currentUser = UserService.getCurrentUser();
  const [step, setStep] = useState(STEP.MENU);

  // Assign Digital Form: the source list is the form templates created in the
  // Form Builder (wo_form_template). Assigning one instantiates a wo_form from
  // it, linked to this WO.
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [formSearch, setFormSearch] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Upload PDF Form
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfMeta, setPdfMeta] = useState({ formName: "", tags: [], description: "", includeInServiceTicket: false });

  // Upload Photo Forms
  const [photos, setPhotos] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  // Mirror of `photos` so the unmount cleanup can revoke the latest preview URLs
  // without re-running (and revoking live URLs) on every edit.
  const photosRef = useRef([]);
  photosRef.current = photos;

  const [uploading, setUploading] = useState(false);

  // Reset everything whenever the modal (re)opens.
  useEffect(() => {
    if (!shown) return;
    setStep(STEP.MENU);
    setTemplates([]);
    setSelectedTemplateId(null);
    setFormSearch("");
    setPdfFile(null);
    setPdfMeta({ formName: "", tags: [], description: "", includeInServiceTicket: false });
    photosRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPhotos([]);
    setPhotoIndex(0);
  }, [shown]);

  // Revoke any outstanding photo preview URLs on unmount to avoid leaks.
  useEffect(() => () => photosRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl)), []);

  const filteredTemplates = useMemo(() => {
    const keyword = formSearch.trim().toLowerCase();
    if (!keyword) return templates;
    return templates.filter((tpl) => tpl.name?.toLowerCase().includes(keyword));
  }, [templates, formSearch]);

  if (!shown) return null;

  const openAssign = () => {
    setStep(STEP.ASSIGN);
    // Source list is the active form templates from the Form Builder.
    FormTemplateService.getAllForSelection("true", "")
      .then((res) => {
        if (res.status === 200) setTemplates(res.data || []);
      })
      .catch((err) => handleError(err));
  };

  const handleAssign = async () => {
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (!template || !order?.id) return;
    setAssigning(true);
    try {
      // Assigning a template instantiates a wo_form from it, linked to this WO
      // and marked assigned (status 1). The template's schema is copied so the
      // form is fillable independently of later template edits.
      const res = await WOFormService.create({
        name: template.name,
        schema: template.schema ?? null,
        formType: template.formType ?? 0,
        frequency: template.frequency ?? null,
        templateId: template.id,
        woId: order.id,
        status: 1,
        createdBy: currentUser?.id,
      });
      if (res.status === 200 || res.status === 201) {
        toast("Form assigned successfully");
        onAssigned?.();
        onClose?.();
      } else {
        toast("Something went wrong");
      }
    } catch (err) {
      handleError(err);
    } finally {
      setAssigning(false);
    }
  };

  // Build the index-aligned metadata array and upload as jobsite forms.
  const uploadForms = async (entries, noun) => {
    if (!entries.length || !order?.id) return;
    setUploading(true);
    try {
      const files = [];
      const metadata = [];
      for (const entry of entries) {
        files.push(await compressIfNeeded(entry.file));
        metadata.push({
          attachmentType: JOBSITE_FORM_TYPE,
          documentName: entry.formName?.trim() || entry.file.name,
          tags: entry.tags,
          description: entry.description?.trim() || "",
          includeInServiceTicket: entry.includeInServiceTicket,
        });
      }
      const res = await WOService.uploadFiles(files, order.id, ATTACHMENT_UPLOAD_FLOW, currentUser, metadata);
      if (res.status === 201) {
        toast(`${files.length} ${noun}${files.length > 1 ? "s" : ""} uploaded successfully`);
        onUploaded?.();
        onClose?.();
      } else {
        toast("Something went wrong");
      }
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
    }
  };

  // ---- Step 1: chooser ----
  if (step === STEP.MENU) {
    const cards = [
      {
        title: "Assign Digital Form",
        desc: "Assign an existing form template created in Makani Form Builder.",
        bg: "bg-[#DAEBFF]",
        onClick: openAssign,
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M15.25 3C15.25 2.86193 15.1381 2.75 15 2.75H9C8.86193 2.75 8.75 2.86193 8.75 3V5C8.75 5.13807 8.86193 5.25 9 5.25H15C15.1381 5.25 15.25 5.13807 15.25 5V3ZM16.75 5C16.75 5.9665 15.9665 6.75 15 6.75H9C8.0335 6.75 7.25 5.9665 7.25 5V3C7.25 2.0335 8.0335 1.25 9 1.25H15C15.9665 1.25 16.75 2.0335 16.75 3V5Z"
              fill="#0065FD"
            />
            <path
              d="M3.25 20V6C3.25 5.27065 3.53994 4.57139 4.05566 4.05566C4.57139 3.53994 5.27065 3.25 6 3.25H8C8.41421 3.25 8.75 3.58579 8.75 4C8.75 4.41421 8.41421 4.75 8 4.75H6C5.66848 4.75 5.35063 4.88179 5.11621 5.11621C4.88179 5.35063 4.75 5.66848 4.75 6V20C4.75 20.3315 4.88179 20.6494 5.11621 20.8838C5.35063 21.1182 5.66848 21.25 6 21.25H18C18.3315 21.25 18.6494 21.1182 18.8838 20.8838C19.1182 20.6494 19.25 20.3315 19.25 20V6C19.25 5.66848 19.1182 5.35063 18.8838 5.11621C18.6494 4.88179 18.3315 4.75 18 4.75H16C15.5858 4.75 15.25 4.41421 15.25 4C15.25 3.58579 15.5858 3.25 16 3.25H18C18.7293 3.25 19.4286 3.53994 19.9443 4.05566C20.4601 4.57139 20.75 5.27065 20.75 6V20C20.75 20.7293 20.4601 21.4286 19.9443 21.9443C19.4286 22.4601 18.7293 22.75 18 22.75H6C5.27065 22.75 4.57139 22.4601 4.05566 21.9443C3.53994 21.4286 3.25 20.7293 3.25 20Z"
              fill="#0065FD"
            />
            <path
              d="M16 10.25C16.4142 10.25 16.75 10.5858 16.75 11C16.75 11.4142 16.4142 11.75 16 11.75H12C11.5858 11.75 11.25 11.4142 11.25 11C11.25 10.5858 11.5858 10.25 12 10.25H16Z"
              fill="#0065FD"
            />
            <path
              d="M16 15.25C16.4142 15.25 16.75 15.5858 16.75 16C16.75 16.4142 16.4142 16.75 16 16.75H12C11.5858 16.75 11.25 16.4142 11.25 16C11.25 15.5858 11.5858 15.25 12 15.25H16Z"
              fill="#0065FD"
            />
            <path
              d="M8.00977 10.25C8.42398 10.25 8.75977 10.5858 8.75977 11C8.75977 11.4142 8.42398 11.75 8.00977 11.75H8C7.58579 11.75 7.25 11.4142 7.25 11C7.25 10.5858 7.58579 10.25 8 10.25H8.00977Z"
              fill="#0065FD"
            />
            <path
              d="M8.00977 15.25C8.42398 15.25 8.75977 15.5858 8.75977 16C8.75977 16.4142 8.42398 16.75 8.00977 16.75H8C7.58579 16.75 7.25 16.4142 7.25 16C7.25 15.5858 7.58579 15.25 8 15.25H8.00977Z"
              fill="#0065FD"
            />
          </svg>
        ),
      },
      {
        title: "Upload PDF Form",
        desc: "Upload a completed PDF form or customer document.",
        bg: "bg-[#FEE2E2]",
        onClick: () => setStep(STEP.PDF_PICK),
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M3.75 15C4.41304 15 5.04893 15.2634 5.51777 15.7322C5.98661 16.2011 6.25 16.837 6.25 17.5C6.25 18.163 5.98661 18.7989 5.51777 19.2678C5.04893 19.7366 4.41304 20 3.75 20H2.5V21.25C2.5 21.4489 2.42098 21.6397 2.28033 21.7803C2.13968 21.921 1.94891 22 1.75 22C1.55109 22 1.36032 21.921 1.21967 21.7803C1.07902 21.6397 1 21.4489 1 21.25V15.75C1 15.5511 1.07902 15.3603 1.21967 15.2197C1.36032 15.079 1.55109 15 1.75 15H3.75ZM2.5 18.5H3.75C4.01522 18.5 4.26957 18.3946 4.45711 18.2071C4.64464 18.0196 4.75 17.7652 4.75 17.5C4.75 17.2348 4.64464 16.9804 4.45711 16.7929C4.26957 16.6054 4.01522 16.5 3.75 16.5H2.5V18.5ZM9.25 15C9.97935 15 10.6788 15.2897 11.1945 15.8055C11.7103 16.3212 12 17.0207 12 17.75V19.25C12 19.9793 11.7103 20.6788 11.1945 21.1945C10.6788 21.7103 9.97935 22 9.25 22H7.75C7.55109 22 7.36032 21.921 7.21967 21.7803C7.07902 21.6397 7 21.4489 7 21.25V15.75C7 15.5511 7.07902 15.3603 7.21967 15.2197C7.36032 15.079 7.55109 15 7.75 15H9.25ZM8.5 20.5H9.25C9.94 20.5 10.5 19.94 10.5 19.25V17.75C10.5 17.06 9.94 16.5 9.25 16.5H8.5V20.5ZM16.75 15C16.9489 15 17.1397 15.079 17.2803 15.2197C17.421 15.3603 17.5 15.5511 17.5 15.75C17.5 15.9489 17.421 16.1397 17.2803 16.2803C17.1397 16.421 16.9489 16.5 16.75 16.5H14.5V18H16.25C16.4489 18 16.6397 18.079 16.7803 18.2197C16.921 18.3603 17 18.5511 17 18.75C17 18.9489 16.921 19.1397 16.7803 19.2803C16.6397 19.421 16.4489 19.5 16.25 19.5H14.5V21.25C14.5 21.4489 14.421 21.6397 14.2803 21.7803C14.1397 21.921 13.9489 22 13.75 22C13.5511 22 13.3603 21.921 13.2197 21.7803C13.079 21.6397 13 21.4489 13 21.25V15.75C13 15.5511 13.079 15.3603 13.2197 15.2197C13.3603 15.079 13.5511 15 13.75 15H16.75ZM12.129 2C12.7255 2.00053 13.2974 2.23792 13.719 2.66L19.341 8.28C19.763 8.702 20 9.274 20 9.871V19.5C20 20.163 19.7366 20.7989 19.2678 21.2678C18.7989 21.7366 18.163 22 17.5 22H15.33C15.4419 21.7658 15.5 21.5096 15.5 21.25V20.5H17.5C17.7652 20.5 18.0196 20.3946 18.2071 20.2071C18.3946 20.0196 18.5 19.7652 18.5 19.5V10H14C13.4696 10 12.9609 9.78929 12.5858 9.41421C12.2107 9.03914 12 8.53043 12 8V3.5H6.5C6.23478 3.5 5.98043 3.60536 5.79289 3.79289C5.60536 3.98043 5.5 4.23478 5.5 4.5V14H4V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2H12.129ZM13.5 8C13.5 8.13261 13.5527 8.25979 13.6464 8.35355C13.7402 8.44732 13.8674 8.5 14 8.5H17.44L13.5 4.56V8Z"
              fill="#EF4444"
            />
          </svg>
        ),
      },
      {
        title: "Upload Photo Form",
        desc: "Upload photos of completed paper forms.",
        bg: "bg-[#F3E8FF]",
        onClick: () => setStep(STEP.PHOTO_PICK),
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="18" viewBox="0 0 22 18" fill="none">
            <path
              d="M20 5.75C20 5.41848 19.8682 5.10063 19.6338 4.86621C19.3994 4.63179 19.0815 4.5 18.75 4.5H15.75C15.5275 4.5 15.3163 4.40135 15.1738 4.23047L12.8984 1.5H8.60156L6.32617 4.23047C6.18368 4.40135 5.9725 4.5 5.75 4.5H2.75C2.41848 4.5 2.10063 4.63179 1.86621 4.86621C1.63179 5.10063 1.5 5.41848 1.5 5.75V14.75C1.5 15.0815 1.63179 15.3994 1.86621 15.6338C2.10063 15.8682 2.41848 16 2.75 16H18.75C19.0815 16 19.3994 15.8682 19.6338 15.6338C19.8682 15.3994 20 15.0815 20 14.75V5.75ZM21.5 14.75C21.5 15.4793 21.2101 16.1786 20.6943 16.6943C20.1786 17.2101 19.4793 17.5 18.75 17.5H2.75C2.02065 17.5 1.32139 17.2101 0.805664 16.6943C0.289939 16.1786 0 15.4793 0 14.75V5.75C0 5.02065 0.289939 4.32139 0.805664 3.80566C1.32139 3.28994 2.02065 3 2.75 3H5.39844L7.67383 0.269531L7.73047 0.208984C7.86936 0.0755933 8.05531 0 8.25 0H13.25C13.4725 0 13.6837 0.0986549 13.8262 0.269531L16.1016 3H18.75C19.4793 3 20.1786 3.28994 20.6943 3.80566C21.2101 4.32139 21.5 5.02065 21.5 5.75V14.75Z"
              fill="#A855F7"
            />
            <path
              d="M13 9.75C13 8.50736 11.9926 7.5 10.75 7.5C9.50736 7.5 8.5 8.50736 8.5 9.75C8.5 10.9926 9.50736 12 10.75 12C11.9926 12 13 10.9926 13 9.75ZM14.5 9.75C14.5 11.8211 12.8211 13.5 10.75 13.5C8.67893 13.5 7 11.8211 7 9.75C7 7.67893 8.67893 6 10.75 6C12.8211 6 14.5 7.67893 14.5 9.75Z"
              fill="#A855F7"
            />
          </svg>
        ),
      },
    ];

    return (
      <ModalShell
        title="Add Form"
        subtitle={vocabularize("Choose how you would like to add a form to this Work Order.")}
        onClose={onClose}
        width="w-[600px]"
        footer={<SecondaryButton onClick={onClose}>Cancel</SecondaryButton>}
      >
        <div className="p-8 flex flex-col gap-4">
          {cards.map((card) => (
            <button
              key={card.title}
              type="button"
              onClick={card.onClick}
              className="w-full flex items-center gap-4 p-5 rounded-[14px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] hover:outline-[#0065FD] transition-colors text-left cursor-pointer"
            >
              <div className={`w-[48px] h-[48px] rounded-[14px] ${card.bg} flex items-center justify-center shrink-0`}>
                {card.icon}
              </div>
              <div className="flex-1 min-w-0 flex flex-col">
                <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]">
                  {card.title}
                </span>
                <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
                  {card.desc}
                </span>
              </div>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
                <path
                  d="M9.75 6l6 6-6 6"
                  stroke="#7C829A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ))}
        </div>
      </ModalShell>
    );
  }

  // ---- Assign Digital Form: "Select Form Template" ----
  if (step === STEP.ASSIGN) {
    const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
    return (
      <ModalShell
        title="Select Form"
        onClose={onClose}
        width="w-[900px]"
        footer={
          <>
            <span className="mr-auto font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]">
              {selectedTemplate ? `Selected: ${selectedTemplate.name}` : ""}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="h-[40px] px-1 rounded-[6px] flex items-center justify-center font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#7C829A] cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAssign}
              disabled={!selectedTemplateId || assigning}
              className={`h-[40px] px-4 rounded-[8px] bg-[#0065FD] flex items-center justify-center gap-2 font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-white ${
                !selectedTemplateId || assigning ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              {assigning ? (
                <Oval height="16" width="16" color="#FFFFFF" strokeWidth={8} secondaryColor="#FFEEFF" visible />
              ) : (
                "Assign Form"
              )}
            </button>
          </>
        }
      >
        <div className="px-6 py-6 h-[60vh] flex flex-col gap-6">
          {/* Search stays fixed while the list below scrolls. */}
          <div className="shrink-0 w-full flex items-center gap-2 h-[40px] px-3 bg-white rounded-[8px] outline outline-1 -outline-offset-1 outline-[#C9CEE2]">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="#68778D" strokeWidth="1.6" />
              <path d="M14 14l3 3" stroke="#68778D" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input
              value={formSearch}
              onChange={(e) => setFormSearch(e.target.value)}
              placeholder="Search forms..."
              className="flex-1 outline-none font-OpenSansRegular text-[14px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
            />
          </div>

          <div className="flex-1 min-h-0 w-full flex flex-col bg-white rounded-[12px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] shadow-[0px_1px_2px_rgba(0,0,0,0.06)] overflow-hidden">
            <div
              className="scrollbar-hide shrink-0 grid grid-cols-[minmax(0,1fr)_180px_120px] items-center gap-3 h-[44px] px-4 bg-[#F8F9FE] border-b border-[#E5E8F3]"
              style={{ scrollbarGutter: "stable", overflowY: "scroll" }}
            >
              <span className="font-OpenSansSemiBold text-[12px] leading-[18px] text-[#5F6477]">Form Name</span>
              <span className="font-OpenSansSemiBold text-[12px] leading-[18px] text-[#5F6477]">Category</span>
              <span className="font-OpenSansSemiBold text-[12px] leading-[18px] text-[#5F6477] text-right">
                Last Updated
              </span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarGutter: "stable" }}>
              {filteredTemplates.length === 0 ? (
                <div className="px-4 py-6">
                  <span className="font-OpenSansRegular text-[14px] text-[#5F6477]">No forms available</span>
                </div>
              ) : (
                filteredTemplates.map((tpl) => {
                  const selected = tpl.id === selectedTemplateId;
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setSelectedTemplateId(tpl.id)}
                      className={`w-full grid grid-cols-[minmax(0,1fr)_180px_120px] items-center gap-3 h-[54px] px-4 border-b border-[#E5E8F3] last:border-b-0 text-left ${
                        selected ? "bg-[#F5F9FF]" : "hover:bg-[#F8F9FE]"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0 ${
                            selected ? "bg-[#0065FD]" : "bg-white border border-[#C9CEE2]"
                          }`}
                        >
                          {selected && (
                            <span className="w-[9px] h-[9px] rounded-full bg-white shadow-[0px_2px_60px_rgba(26,30,35,0.08)]" />
                          )}
                        </span>
                        <span className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332] truncate">
                          {tpl.name || "Untitled Form"}
                        </span>
                      </div>
                      <span className="inline-flex w-fit px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#0058E5]">
                        {getCategoryLabel(tpl)}
                      </span>
                      <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477] text-right">
                        {tpl.updatedAt ? formatDate(tpl.updatedAt, "MMM D, YYYY") : "-"}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ModalShell>
    );
  }

  // ---- Upload PDF Form: pick ----
  if (step === STEP.PDF_PICK) {
    return (
      <ModalShell
        title="Upload PDF Form"
        onClose={onClose}
        footer={<GhostButton onClick={onClose}>Cancel</GhostButton>}
      >
        <Dropzone
          icon={
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 16V4m0 0L8 8m4-4 4 4"
                stroke="#1A2332"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"
                stroke="#1A2332"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          }
          title="Upload Document"
          hint="Drag & Drop files here"
          footer="Supported formats: PDF"
          accept={PDF_ACCEPT}
          multiple={false}
          onFiles={(files) => {
            const file = files[0];
            if (!file) return;
            setPdfFile(file);
            setPdfMeta({ formName: file.name, tags: [], description: "", includeInServiceTicket: false });
            setStep(STEP.PDF_META);
          }}
        />
      </ModalShell>
    );
  }

  // ---- Upload PDF Form: metadata ----
  if (step === STEP.PDF_META) {
    return (
      <ModalShell
        title="Upload PDF Form"
        onClose={onClose}
        width="w-[600px]"
        footer={
          <>
            <GhostButton onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton
              onClick={() =>
                uploadForms(
                  [
                    {
                      file: pdfFile,
                      formName: pdfMeta.formName,
                      tags: pdfMeta.tags,
                      description: pdfMeta.description,
                      includeInServiceTicket: pdfMeta.includeInServiceTicket,
                    },
                  ],
                  "form",
                )
              }
              disabled={!pdfFile}
              loading={uploading}
            >
              Upload Form
            </PrimaryButton>
          </>
        }
      >
        <div className="p-6 flex flex-col gap-6">
          <div className="flex items-center gap-4 p-4 rounded-[12px] bg-[#F8F9FE] outline outline-1 -outline-offset-1 outline-[#E5E8F3]">
            <div className="w-[48px] h-[48px] shrink-0 rounded-[8px] bg-[#FEE2E2] flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3.75 15C4.41304 15 5.04893 15.2634 5.51777 15.7322C5.98661 16.2011 6.25 16.837 6.25 17.5C6.25 18.163 5.98661 18.7989 5.51777 19.2678C5.04893 19.7366 4.41304 20 3.75 20H2.5V21.25C2.5 21.4489 2.42098 21.6397 2.28033 21.7803C2.13968 21.921 1.94891 22 1.75 22C1.55109 22 1.36032 21.921 1.21967 21.7803C1.07902 21.6397 1 21.4489 1 21.25V15.75C1 15.5511 1.07902 15.3603 1.21967 15.2197C1.36032 15.079 1.55109 15 1.75 15H3.75ZM2.5 18.5H3.75C4.01522 18.5 4.26957 18.3946 4.45711 18.2071C4.64464 18.0196 4.75 17.7652 4.75 17.5C4.75 17.2348 4.64464 16.9804 4.45711 16.7929C4.26957 16.6054 4.01522 16.5 3.75 16.5H2.5V18.5ZM9.25 15C9.97935 15 10.6788 15.2897 11.1945 15.8055C11.7103 16.3212 12 17.0207 12 17.75V19.25C12 19.9793 11.7103 20.6788 11.1945 21.1945C10.6788 21.7103 9.97935 22 9.25 22H7.75C7.55109 22 7.36032 21.921 7.21967 21.7803C7.07902 21.6397 7 21.4489 7 21.25V15.75C7 15.5511 7.07902 15.3603 7.21967 15.2197C7.36032 15.079 7.55109 15 7.75 15H9.25ZM8.5 20.5H9.25C9.94 20.5 10.5 19.94 10.5 19.25V17.75C10.5 17.06 9.94 16.5 9.25 16.5H8.5V20.5ZM16.75 15C16.9489 15 17.1397 15.079 17.2803 15.2197C17.421 15.3603 17.5 15.5511 17.5 15.75C17.5 15.9489 17.421 16.1397 17.2803 16.2803C17.1397 16.421 16.9489 16.5 16.75 16.5H14.5V18H16.25C16.4489 18 16.6397 18.079 16.7803 18.2197C16.921 18.3603 17 18.5511 17 18.75C17 18.9489 16.921 19.1397 16.7803 19.2803C16.6397 19.421 16.4489 19.5 16.25 19.5H14.5V21.25C14.5 21.4489 14.421 21.6397 14.2803 21.7803C14.1397 21.921 13.9489 22 13.75 22C13.5511 22 13.3603 21.921 13.2197 21.7803C13.079 21.6397 13 21.4489 13 21.25V15.75C13 15.5511 13.079 15.3603 13.2197 15.2197C13.3603 15.079 13.5511 15 13.75 15H16.75ZM12.129 2C12.7255 2.00053 13.2974 2.23792 13.719 2.66L19.341 8.28C19.763 8.702 20 9.274 20 9.871V19.5C20 20.163 19.7366 20.7989 19.2678 21.2678C18.7989 21.7366 18.163 22 17.5 22H15.33C15.4419 21.7658 15.5 21.5096 15.5 21.25V20.5H17.5C17.7652 20.5 18.0196 20.3946 18.2071 20.2071C18.3946 20.0196 18.5 19.7652 18.5 19.5V10H14C13.4696 10 12.9609 9.78929 12.5858 9.41421C12.2107 9.03914 12 8.53043 12 8V3.5H6.5C6.23478 3.5 5.98043 3.60536 5.79289 3.79289C5.60536 3.98043 5.5 4.23478 5.5 4.5V14H4V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2H12.129ZM13.5 8C13.5 8.13261 13.5527 8.25979 13.6464 8.35355C13.7402 8.44732 13.8674 8.5 14 8.5H17.44L13.5 4.56V8Z"
                  fill="#EF4444"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] truncate">
                {pdfFile?.name}
              </span>
              <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
                {formatBytes(pdfFile?.size)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setPdfFile(null);
                setStep(STEP.PDF_PICK);
              }}
              className="p-2 text-[#7C829A] cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="15" viewBox="0 0 13 15" fill="none">
                <path
                  d="M12.5 2.66667C12.7761 2.66667 13 2.89052 13 3.16667C13 3.44281 12.7761 3.66667 12.5 3.66667H0.5C0.223858 3.66667 0 3.44281 0 3.16667C0 2.89052 0.223858 2.66667 0.5 2.66667H12.5Z"
                  fill="#7C829A"
                />
                <path
                  d="M1.33333 12.5V3.16667C1.33333 2.89052 1.55719 2.66667 1.83333 2.66667C2.10948 2.66667 2.33333 2.89052 2.33333 3.16667V12.5C2.33333 12.6707 2.42369 12.8833 2.60352 13.0632C2.78334 13.243 2.99595 13.3333 3.16667 13.3333H9.83333C10.0041 13.3333 10.2167 13.243 10.3965 13.0632C10.5763 12.8833 10.6667 12.6707 10.6667 12.5V3.16667C10.6667 2.89052 10.8905 2.66667 11.1667 2.66667C11.4428 2.66667 11.6667 2.89052 11.6667 3.16667V12.5C11.6667 12.9959 11.4237 13.45 11.1035 13.7702C10.7833 14.0904 10.3293 14.3333 9.83333 14.3333H3.16667C2.67072 14.3333 2.21666 14.0904 1.89648 13.7702C1.57631 13.45 1.33333 12.9959 1.33333 12.5Z"
                  fill="#7C829A"
                />
                <path
                  d="M8.66667 3.16667V1.83333C8.66667 1.66262 8.57631 1.45001 8.39648 1.27018C8.21666 1.09036 8.00405 1 7.83333 1H5.16667C4.99595 1 4.78334 1.09036 4.60352 1.27018C4.42369 1.45001 4.33333 1.66262 4.33333 1.83333V3.16667C4.33333 3.44281 4.10948 3.66667 3.83333 3.66667C3.55719 3.66667 3.33333 3.44281 3.33333 3.16667V1.83333C3.33333 1.33738 3.57631 0.883324 3.89648 0.563151C4.21666 0.242978 4.67072 0 5.16667 0H7.83333C8.32928 0 8.78334 0.242978 9.10352 0.563151C9.42369 0.883324 9.66667 1.33738 9.66667 1.83333V3.16667C9.66667 3.44281 9.44281 3.66667 9.16667 3.66667C8.89052 3.66667 8.66667 3.44281 8.66667 3.16667Z"
                  fill="#7C829A"
                />
                <path
                  d="M4.66667 10.5V6.5C4.66667 6.22386 4.89052 6 5.16667 6C5.44281 6 5.66667 6.22386 5.66667 6.5V10.5C5.66667 10.7761 5.44281 11 5.16667 11C4.89052 11 4.66667 10.7761 4.66667 10.5Z"
                  fill="#7C829A"
                />
                <path
                  d="M7.33333 10.5V6.5C7.33333 6.22386 7.55719 6 7.83333 6C8.10948 6 8.33333 6.22386 8.33333 6.5V10.5C8.33333 10.7761 8.10948 11 7.83333 11C7.55719 11 7.33333 10.7761 7.33333 10.5Z"
                  fill="#7C829A"
                />
              </svg>
            </button>
          </div>

          <div className="flex flex-col gap-1">
            <FieldLabel>Form Name</FieldLabel>
            <input
              value={pdfMeta.formName}
              onChange={(e) => setPdfMeta((m) => ({ ...m, formName: e.target.value }))}
              className="h-[40px] px-3 border border-[#E5E8F3] rounded-[6px] outline-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]"
            />
          </div>

          <div className="flex flex-col gap-1">
            <FieldLabel>Tag</FieldLabel>
            <TagInput selectedTags={pdfMeta.tags} onChange={(tags) => setPdfMeta((m) => ({ ...m, tags }))} />
          </div>

          <div className="flex flex-col gap-1">
            <FieldLabel>Description (Optional)</FieldLabel>
            <textarea
              value={pdfMeta.description}
              onChange={(e) => setPdfMeta((m) => ({ ...m, description: e.target.value }))}
              placeholder="Add notes..."
              rows={3}
              className="h-[96px] p-3 bg-[#FBFCFF] border border-[#E5E8F3] rounded-[6px] outline-none resize-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
            />
          </div>

          <ServiceTicketToggle
            checked={pdfMeta.includeInServiceTicket}
            onChange={() => setPdfMeta((m) => ({ ...m, includeInServiceTicket: !m.includeInServiceTicket }))}
            label="Include In Service Ticket"
            helper="Attach this form to the customer service ticket"
          />
        </div>
      </ModalShell>
    );
  }

  // ---- Upload Photo Forms: pick ----
  if (step === STEP.PHOTO_PICK) {
    return (
      <ModalShell
        title="Upload Photo Forms"
        onClose={onClose}
        footer={<GhostButton onClick={onClose}>Cancel</GhostButton>}
      >
        <Dropzone
          icon={
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="7" width="18" height="13" rx="2" stroke="#1A2332" strokeWidth="2" />
              <circle cx="12" cy="13" r="3" stroke="#1A2332" strokeWidth="2" />
              <path d="M8 7l1.5-2h5L16 7" stroke="#1A2332" strokeWidth="2" strokeLinejoin="round" />
            </svg>
          }
          title="Upload Photos"
          hint="Drag & Drop files here"
          footer="Supported formats: JPG, PNG, HEIC — multiple files supported"
          accept={PHOTO_ACCEPT}
          multiple
          onFiles={(files) => {
            setPhotos(files.map(makePhotoEntry));
            setPhotoIndex(0);
            setStep(STEP.PHOTO_META);
          }}
        />
      </ModalShell>
    );
  }

  // ---- Upload Photo Forms: per-photo metadata wizard ----
  if (step === STEP.PHOTO_META) {
    const photo = photos[photoIndex];
    if (!photo) return null;
    const isLast = photoIndex === photos.length - 1;
    const updatePhoto = (patch) => setPhotos((prev) => prev.map((p, i) => (i === photoIndex ? { ...p, ...patch } : p)));

    return (
      <ModalShell
        title="Upload Photo Forms"
        onClose={onClose}
        width="w-[800px]"
        footer={
          <div className="flex-1 flex items-center justify-between">
            <GhostButton onClick={onClose}>Cancel</GhostButton>
            <div className="flex items-center gap-3">
              {photoIndex > 0 && (
                <SecondaryButton onClick={() => setPhotoIndex((i) => i - 1)}>Previous</SecondaryButton>
              )}
              {isLast ? (
                <PrimaryButton onClick={() => uploadForms(photos, "photo")} loading={uploading}>
                  Upload {photos.length > 1 ? `${photos.length} Photos` : "Photo"}
                </PrimaryButton>
              ) : (
                <PrimaryButton onClick={() => setPhotoIndex((i) => i + 1)}>Next</PrimaryButton>
              )}
            </div>
          </div>
        }
      >
        <div className="px-6 pt-6 flex items-center justify-between">
          <span className="font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332]">
            {photoIndex + 1} Of {photos.length} Photos
          </span>
          <label className="h-[40px] px-1 flex items-center gap-2 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#0065FD] cursor-pointer">
            <IoAdd size={16} />
            Add More
            <input
              type="file"
              accept={PHOTO_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) setPhotos((prev) => [...prev, ...files.map(makePhotoEntry)]);
                if (e.target) e.target.value = null;
              }}
            />
          </label>
        </div>

        <div className="px-6 py-6 flex items-start gap-8">
          <div className="relative flex-1 self-stretch max-h-[378px] rounded-[8px] overflow-hidden bg-[#F8F9FE] outline outline-1 -outline-offset-1 outline-[#E5E8F3] flex items-center justify-center">
            <img src={photo.previewUrl} alt={photo.formName} className="w-full h-full object-contain" />
            <span className="absolute left-4 bottom-4 px-2 py-1 rounded-[4px] bg-black/60 font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-white truncate max-w-[90%]">
              {photo.formName}
            </span>
          </div>

          <div className="w-[340px] flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <FieldLabel>Form Name</FieldLabel>
              <input
                value={photo.formName}
                onChange={(e) => updatePhoto({ formName: e.target.value })}
                className="h-[40px] px-3 bg-white rounded-[6px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]"
              />
            </div>

            <div className="flex flex-col gap-1">
              <FieldLabel>Tag</FieldLabel>
              <TagInput selectedTags={photo.tags} onChange={(tags) => updatePhoto({ tags })} />
            </div>

            <div className="flex flex-col gap-1">
              <FieldLabel>Description (Optional)</FieldLabel>
              <textarea
                value={photo.description}
                onChange={(e) => updatePhoto({ description: e.target.value })}
                placeholder="Add notes..."
                rows={3}
                className="h-[96px] p-3 bg-[#FBFCFF] rounded-[6px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] resize-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#7C829A]"
              />
            </div>

            <ServiceTicketToggle
              checked={photo.includeInServiceTicket}
              onChange={() => updatePhoto({ includeInServiceTicket: !photo.includeInServiceTicket })}
              label="Include In Service Ticket"
              helper="Photo will be visible to customer"
            />
          </div>
        </div>
      </ModalShell>
    );
  }

  return null;
};

export default AddFormModal;
